// Обзор: сколько прогонов, сколько сломанных seed, медианы ключевых
// величин — и ползунки, которыми выборку пересчитывают.

import { useMemo, useState } from 'react';
import { useStore, DEFAULT_SEED_FROM, DEFAULT_SIMULATE_RUNS } from '../data/store';
import { Callout, CopyButton, OriginTag, Panel, Tile } from '../components/Ui';
import { SliderPanel, cliCommand } from '../components/SliderPanel';
import { BoxPlots, FrequencySparkline } from '../components/Charts';
import {
  METRICS,
  TIGHT_DISTINCT_MAX,
  describe,
  distinctCount,
  frequencies,
  isTight,
} from '../lib/stats';
import { num, percent, runsWord, signed } from '../lib/format';
import type { BalanceConfig } from '../lib/types';

export function Overview() {
  const store = useStore();
  const { summary, active, simulation, summaryFromSimulation } = store;
  const [runs, setRuns] = useState(DEFAULT_SIMULATE_RUNS);
  const [seedFrom, setSeedFrom] = useState(DEFAULT_SEED_FROM);
  const [lastConfig, setLastConfig] = useState<BalanceConfig | null>(null);

  const online = store.server?.online ?? false;
  const enemies = summary.metrics.get('enemies');
  const ammo = summary.metrics.get('ammoTotal');
  const margin = summary.metrics.get('ammoMargin');
  const rooms = summary.metrics.get('rooms');
  const floor = summary.metrics.get('floorCells');
  const medkits = summary.metrics.get('medkits');

  // Форма считается по строкам датасета: /simulate отдаёт сводку без строк.
  const shapes = useMemo(
    () =>
      active.length === 0
        ? []
        : METRICS.map((metric) => {
            const values = active.map(metric.pick);
            return {
              metric,
              values,
              rows: frequencies(values),
              stats: describe(values),
              distinct: distinctCount(values),
              wide: !isTight(values),
            };
          }),
    [active],
  );
  const wideShapes = useMemo(() => shapes.filter((s) => s.wide), [shapes]);
  const wideLabels = wideShapes.map((s) => s.metric.label);
  // Массив, собранный прямо в JSX, — новый объект на каждую перерисовку,
  // а по нему график пересобирается целиком (см. PlotFigure).
  const boxRows = useMemo(
    () => wideShapes.map((s) => ({ label: s.metric.label, values: s.values })),
    [wideShapes],
  );

  const brokenTone = summary.brokenShare > 0.05 ? 'bad' : summary.brokenShare > 0 ? 'warn' : 'good';
  const deficitHeld = 1 - summary.ammoSurplusShare;

  return (
    <div className="page">
      <div className="page-head">
        <h1>Обзор</h1>
        <p>
          Выборка: <b className="mono">{store.activeLabel}</b>. Ни одна плитка не показывает среднего
          без распределения — медиана и перцентили ниже, полные гистограммы на странице «Распределения».
        </p>
        <OriginTag origin={summaryFromSimulation ? 'server' : store.runsOrigin} />
      </div>

      {summary.configHashes.length > 1 && (
        <Callout title="В выборке перемешаны разные конфиги" tone="hard">
          <p>
            Найдено отпечатков: <b className="mono">{summary.configHashes.join(', ')}</b>. По разделу 5
            контракта это ошибка, а не предупреждение: складывать такие строки в одну статистику нельзя,
            и все числа ниже посчитаны по смеси двух разных игр.
          </p>
        </Callout>
      )}

      <div className="tiles">
        <Tile
          label="Прогонов"
          value={num(summary.rows)}
          note={
            <>
              отпечаток <span className="mono">{summary.configHash ?? '—'}</span>
            </>
          }
        />
        <Tile
          label="Сломанных seed"
          value={percent(summary.brokenShare)}
          tone={brokenTone}
          note={`${num(Math.round(summary.brokenShare * summary.rows))} из ${num(summary.rows)} — флаг или нарушенный дефицит`}
        />
        <Tile
          label="Дефицит соблюдён"
          value={percent(deficitHeld)}
          tone={deficitHeld >= 1 ? 'good' : deficitHeld > 0.95 ? 'warn' : 'bad'}
          note="патронов меньше, чем нужно жадному профилю"
        />
        <Tile
          label="Врагов, медиана"
          value={num(enemies?.p50 ?? Number.NaN)}
          note={`p10 ${num(enemies?.p10 ?? NaN)} · p90 ${num(enemies?.p90 ?? NaN)}`}
        />
        <Tile
          label="Патронов, медиана"
          value={num(ammo?.p50 ?? Number.NaN)}
          note={`p10 ${num(ammo?.p10 ?? NaN)} · p90 ${num(ammo?.p90 ?? NaN)}`}
        />
        <Tile
          label="Запас патронов, медиана"
          value={signed(margin?.p50 ?? Number.NaN)}
          tone={(margin?.p50 ?? 0) < 0 ? 'good' : 'bad'}
          note="отрицательное — норма: игрок беднее, чем ему хочется"
        />
        <Tile
          label="Комнат, медиана"
          value={num(rooms?.p50 ?? Number.NaN)}
          note={`p10 ${num(rooms?.p10 ?? NaN)} · p90 ${num(rooms?.p90 ?? NaN)}`}
        />
        <Tile
          label="Площадь пола, медиана"
          value={num(floor?.p50 ?? Number.NaN)}
          unit="кл"
          note={`p10 ${num(floor?.p10 ?? NaN)} · p90 ${num(floor?.p90 ?? NaN)}`}
        />
        <Tile
          label="Аптечек, медиана"
          value={num(medkits?.p50 ?? Number.NaN)}
          note={`p10 ${num(medkits?.p10 ?? NaN)} · p90 ${num(medkits?.p90 ?? NaN)}`}
        />
        <Tile
          label="Запасная планировка"
          value={percent(summary.layoutFallbackShare)}
          tone={summary.layoutFallbackShare > 0 ? 'warn' : 'good'}
          note="доля прогонов, где генератор ушёл на запасной путь"
        />
      </div>

      <SliderPanel
        presets={store.presets}
        runs={runs}
        seedFrom={seedFrom}
        onRunsChange={setRuns}
        onSeedFromChange={setSeedFrom}
        onChange={(config) => {
          setLastConfig(config);
          if (online) store.requestSimulation(config, runs, seedFrom);
        }}
        onReset={() => {
          setLastConfig(null);
          store.clearSimulation();
        }}
        serverOnline={online}
        pending={simulation.pending}
        error={simulation.error}
        elapsedMs={simulation.result?.roundTripMs ?? null}
        serverMs={simulation.result?.durationMs ?? null}
        cacheHit={simulation.result?.cacheHit ?? false}
        simulated={summaryFromSimulation}
      />

      {!online && lastConfig && (
        <Panel
          title="Прогнать этот конфиг руками"
          aside={<CopyButton text={cliCommand(lastConfig, runs, seedFrom)} label="копировать конфиг и команду" />}
        >
          <pre className="cmd">{cliCommand(lastConfig, runs, seedFrom)}</pre>
        </Panel>
      )}

      {summaryFromSimulation && (
        <Callout tone="info" title="Показана сводка пересчёта, а не строки прогонов">
          <p>
            Плитки и перцентили выше посчитаны сервером по{' '}
            <b className="mono">{num(summary.rows)}</b> свежим прогонам конфига{' '}
            <span className="mono">{simulation.result?.configHash ?? '—'}</span>
            {simulation.result?.cacheHit && ' (ответ из кэша сервера)'}. Строк по каждому seed
            /simulate не отдаёт, поэтому «Распределения», «Аномалии» и «Сравнение» продолжают
            показывать выбранный датасет <span className="mono">{store.dataset}</span> — а не молча
            подмешивают к нему пересчёт.
          </p>
          {(simulation.result?.anomalyPhrases.length ?? 0) > 0 && (
            <p className="dim">
              Сервер нашёл в пересчёте аномалий: {num(simulation.result?.anomalyTotal ?? 0)}. Первая —{' '}
              {simulation.result?.anomalyPhrases[0]}
            </p>
          )}
        </Callout>
      )}

      <Panel
        title="Форма каждой величины"
        aside={
          <span className="dim">
            столбик — одно целое значение, янтарная риска — медиана, янтарный столбик — реже 1%
          </span>
        }
      >
        {summaryFromSimulation && (
          <p className="warn" style={{ marginTop: 0, fontSize: 'var(--fs-xs)' }}>
            Формы ниже построены по строкам датасета{' '}
            <span className="mono">{store.dataset}</span>: /simulate отдаёт сводку без строк, и
            рисовать по ней форму распределения было бы догадкой. Плитки выше — из пересчёта.
          </p>
        )}

        {shapes.length === 0 ? (
          <div className="empty">строк выборки нет</div>
        ) : (
          <div className="shapes">
            {shapes.map((shape) => (
              <div className="shape-row" key={shape.metric.key}>
                <div className="name">
                  {shape.metric.label}
                  <small>{shape.metric.unit}</small>
                </div>
                <FrequencySparkline
                  rows={shape.rows}
                  stats={shape.stats}
                  unit={shape.metric.unit}
                  color={shape.wide ? 'var(--series-b)' : 'var(--series-a)'}
                />
                <div className="stats">
                  p10 <b>{num(shape.stats.p10)}</b> · p25 <b>{num(shape.stats.p25)}</b> · p50{' '}
                  <b className="good">{num(shape.stats.p50)}</b> · p75 <b>{num(shape.stats.p75)}</b> ·
                  p90 <b>{num(shape.stats.p90)}</b>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="dim" style={{ marginBottom: 0, marginTop: 10 }}>
          Каждая величина в своей шкале — общей быть не может: врагов шесть, а клеток пола под сотню,
          и в одной оси восемь величин из десяти схлопнулись бы в точку. Величины с широким
          распределением{' '}
          {wideLabels.length > 0 ? (
            <span className="mono">({wideLabels.join(', ')})</span>
          ) : (
            <>(таких здесь нет)</>
          )}{' '}
          отмечены синим, и для них ниже есть настоящий ящик с усами.
        </p>
      </Panel>

      {wideShapes.length > 0 && (
        <Panel
          title="Широкие величины: ящик с усами"
          aside={
            <span className="dim">
              коробка p25…p75, риска — медиана, усы 1.5×IQR, точки за ними — выбросы
            </span>
          }
        >
          <BoxPlots rows={boxRows} />
          <p className="dim" style={{ marginBottom: 0, marginTop: 8 }}>
            Ящик показан только тем величинам, у которых различных значений больше{' '}
            {num(TIGHT_DISTINCT_MAX)}. Для дискретной величины на шесть значений усы нарисовали бы
            хвост, которого нет, — это доказано на странице «Распределения».
          </p>
        </Panel>
      )}

      <Panel title="Перцентили по всем величинам" flush>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Величина</th>
                <th className="n">мин</th>
                <th className="n">p10</th>
                <th className="n">p25</th>
                <th className="n">p50</th>
                <th className="n">p75</th>
                <th className="n">p90</th>
                <th className="n">макс</th>
                <th className="n">среднее</th>
                <th className="n">MAD</th>
              </tr>
            </thead>
            <tbody>
              {METRICS.map((metric) => {
                const stat = summary.metrics.get(metric.key);
                if (!stat) return null;
                return (
                  <tr key={metric.key}>
                    <td>{metric.label}</td>
                    <td className="n">{num(stat.min)}</td>
                    <td className="n">{num(stat.p10)}</td>
                    <td className="n">{num(stat.p25)}</td>
                    <td className="n good">{num(stat.p50)}</td>
                    <td className="n">{num(stat.p75)}</td>
                    <td className="n">{num(stat.p90)}</td>
                    <td className="n">{num(stat.max)}</td>
                    <td className="n dim">{num(stat.mean, 2)}</td>
                    <td className="n faint">{num(stat.mad, 2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Флаги прогонов">
        {summary.flagCounts.length === 0 ? (
          <p className="dim" style={{ margin: 0 }}>
            Ни один из {runsWord(active.length)} не поднял флага. Это хороший результат, а не пустая
            таблица: флаги ставит сам <span className="mono">spusk-sim</span> по разделу 1 контракта.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Флаг</th>
                <th className="n">прогонов</th>
                <th className="n">доля</th>
              </tr>
            </thead>
            <tbody>
              {summary.flagCounts.map((entry) => (
                <tr key={entry.flag}>
                  <td className="mono">{entry.flag}</td>
                  <td className="n">{num(entry.count)}</td>
                  <td className="n">{percent(entry.count / Math.max(1, summary.rows))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
